import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaSearch, FaEye, FaTrash, FaUserGraduate, 
  FaChevronDown, FaChevronUp, FaEdit, FaFileExcel, FaUserPlus, FaFilePdf
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx-js-style';
import api, { BASE_URL } from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import useColleges from '../../../../utils/useColleges';
import './ViewStudent.css';

const API_BASE = BASE_URL;

const colSpanFull = 10;

// Get current year directly from student record
const getStudentYear = (s) => s.currentYear || '—';

const ViewStudent = ({ readOnly = false }) => {
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin' || (user?.colleges?.length > 0);
  const isClerk   = user?.role === 'clerk';
  const { colleges, loading: collegesLoading } = useColleges();
  const navigate  = useNavigate();
  const basePath  = user?.role === 'collegeadmin' ? '/collegeadmin' : user?.role === 'clerk' ? '/clerk' : '/admin';
  const viewOnly  = readOnly || isClerk;

  const [selectedCollege, setSelectedCollege] = useState('');
  const [sessions, setSessions]               = useState([]);
  const [classes, setClasses]                 = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedClass, setSelectedClass]     = useState('');
  const [search, setSearch]                   = useState('');
  const [students, setStudents]               = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [expandedRow, setExpandedRow]         = useState(null);

  // Auto-set college
  useEffect(() => {
    if (!isAdmin && colleges.length > 0) setSelectedCollege(String(colleges[0]._id));
    else if (isAdmin && colleges.length === 1) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);

  // Load Sessions and Classes when College changes
  useEffect(() => {
    if (!selectedCollege) { 
      setSessions([]); 
      setClasses([]); 
      setSelectedSession(''); 
      setSelectedClass(''); 
      return; 
    }

    api.get('/sessions', { params: { college: selectedCollege } })
      .then(r => {
        const data = r.data.data || [];
        setSessions(data);
        // Auto-select active session
        const active = data.find(s => s.isActive);
        if (active) setSelectedSession(active._id);
        else if (data.length > 0) setSelectedSession(data[0]._id);
      })
      .catch(() => setSessions([]));

    api.get('/classes', { params: { college: selectedCollege } })
      .then(r => setClasses(r.data.data || []))
      .catch(() => setClasses([]));
      
    setSelectedClass('');
  }, [selectedCollege]);

  // Load Students
  useEffect(() => {
    if (!selectedCollege) { setStudents([]); return; }
    setLoading(true);
    const params = { college: selectedCollege, limit: 1000 };
    if (selectedSession) params.session = selectedSession;
    if (selectedClass)   params.class   = selectedClass;
    
    api.get('/students', { params })
      .then(r => setStudents(r.data.data || []))
      .catch(() => setStudents([]))
      .finally(() => setLoading(false));
  }, [selectedCollege, selectedSession, selectedClass]);

  // Client-side Search Filtering
  const filteredStudents = useMemo(() => {
    if (!search.trim()) return students;
    const q = search.toLowerCase();
    return students.filter(s => 
      s.name?.toLowerCase().includes(q) || 
      s.admissionNo?.toLowerCase().includes(q)
    );
  }, [students, search]);

  // Export to Excel with professional formatting
  const handleExport = () => {
    if (!selectedCollege) {
      Swal.fire('Please Select College', 'Please select a college before exporting.', 'warning');
      return;
    }
    
    if (filteredStudents.length === 0) {
      Swal.fire('No Data', 'No students to export.', 'info');
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const headers = [
      'S.No', 'Admission No', 'Student Name', 'Gender', 'Class / Course', 'Medium',
      'Admission Year', 'Current Session', 'Current Year / Status', 'Program Duration',
      'Contact Number', 'Email', 'Father Name', 'Father Mobile Number',
      'Mother Name', 'Address', 'City', 'State'
    ];

    const activeCollege = colleges.find(c => c._id === selectedCollege)?.name || 'All Colleges';
    const activeSession = sessions.find(s => s._id === selectedSession)?.name || 'All';
    const activeClass = classes.find(c => c._id === selectedClass)?.name || 'All';
    
    const filtersText = `Filters Applied -> College: ${activeCollege} | Session: ${activeSession} | Class: ${activeClass} | Search: ${search || 'None'}`;

    const colsCount = headers.length;
    const aoa = [
      ['STUDENT DATA EXPORT REPORT', ...Array(colsCount - 1).fill('')],
      [filtersText, ...Array(colsCount - 1).fill('')],
      Array(colsCount).fill('')
    ];

    let merges = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colsCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colsCount - 1 } }
    ];

    let currentRow = 3;

    // Group students by college
    const collegeGroups = {};
    filteredStudents.forEach(s => {
      // Use college from student object, or fall back to selected college filter
      let collegeName = s.college?.name;
      if (!collegeName && selectedCollege) {
        collegeName = colleges.find(c => c._id === selectedCollege)?.name;
      }
      collegeName = collegeName || 'Unknown College';
      
      if (!collegeGroups[collegeName]) collegeGroups[collegeName] = [];
      collegeGroups[collegeName].push(s);
    });

    Object.entries(collegeGroups).sort().forEach(([collegeName, students]) => {
      // College header
      aoa.push([collegeName.toUpperCase(), ...Array(colsCount - 1).fill('')]);
      merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: colsCount - 1 } });
      currentRow++;

      // Column headers
      aoa.push(headers);
      currentRow++;

      // Student rows
      students.forEach((s, index) => {
        aoa.push([
          index + 1,
          s.admissionNo || '—',
          s.name || '—',
          s.gender || '—',
          s.class?.name || s.className || '—',
          s.class?.medium || s.medium || '—',
          s.admissionYear || '—',
          s.session?.name || '—',
          s.currentYear || '—',
          s.class?.duration || '—',
          s.contact || s.phone || '—',
          s.email || '—',
          s.fatherName || '—',
          s.fatherContact || '—',
          s.motherName || '—',
          s.address || '—',
          s.city || '—',
          s.state || '—'
        ]);
        currentRow++;
      });

      // Empty row between colleges
      aoa.push(Array(colsCount).fill(''));
      currentRow++;
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges;
    ws['!cols'] = [
      { wch: 8 },  { wch: 18 }, { wch: 25 }, { wch: 10 }, { wch: 20 },
      { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 },
      { wch: 15 }, { wch: 25 }, { wch: 25 }, { wch: 18 },
      { wch: 25 }, { wch: 35 }, { wch: 15 }, { wch: 15 }
    ];

    const border = {
      top: { style: 'thin', color: { auto: 1 } },
      bottom: { style: 'thin', color: { auto: 1 } },
      left: { style: 'thin', color: { auto: 1 } },
      right: { style: 'thin', color: { auto: 1 } }
    };

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[ref]) ws[ref] = { v: '', t: 's' };

        // Title row (row 0)
        if (R === 0) {
          ws[ref].s = {
            font: { name: 'Arial', sz: 16, bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1E3A5F' } },
            alignment: { vertical: 'center', horizontal: 'center' }
          };
        }
        // Filter info row (row 1)
        else if (R === 1) {
          ws[ref].s = {
            font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '2C3E50' } },
            fill: { fgColor: { rgb: 'F1F5F9' } },
            alignment: { vertical: 'center', horizontal: 'center' },
            border
          };
        }
        // College name headers (uppercase text in column 0, not a header row)
        else if (ws[ref].v && typeof ws[ref].v === 'string' && ws[ref].v === ws[ref].v.toUpperCase() && C === 0 && !headers.includes(ws[ref].v) && R > 2) {
          ws[ref].s = {
            font: { name: 'Arial', sz: 13, bold: true, color: { rgb: '1E3A5F' } },
            fill: { fgColor: { rgb: 'E2E8F0' } },
            alignment: { vertical: 'center', horizontal: 'center' },
            border
          };
        }
        // Column headers
        else if (headers.includes(ws[ref].v)) {
          ws[ref].s = {
            font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '34495E' } },
            alignment: { vertical: 'center', horizontal: 'center' },
            border
          };
        }
        // Data cells
        else if (R > 2 && ws[ref].v !== '') {
          // Admission number column (column 1) - no wrap
          if (C === 1) {
            ws[ref].s = {
              font: { name: 'Arial', sz: 9 },
              alignment: { vertical: 'center', horizontal: 'center', wrapText: false },
              border
            };
          } else {
            ws[ref].s = {
              font: { name: 'Arial', sz: 9 },
              alignment: { vertical: 'center', horizontal: 'center', wrapText: true },
              border
            };
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    XLSX.writeFile(wb, `Students_Export_${today}.xlsx`);
    Swal.fire('Exported!', `${filteredStudents.length} student(s) exported successfully.`, 'success');
  };

  // Toggle Active/Inactive Status
  const handleToggleStatus = async (s) => {
    try {
      // amazonq-ignore-next-line
      await api.put(`/students/${s._id}`, { isActive: !s.isActive });
      setStudents(prev => prev.map(st => st._id === s._id ? { ...st, isActive: !s.isActive } : st));
    } catch { 
      Swal.fire('Error', 'Failed to update status.', 'error'); 
    }
  };

  // Delete Student - Check payments FIRST before confirmation
  const handleDelete = async (id, name) => {
    try {
      // 🔍 STEP 1: Check if student has payments FIRST (before any confirmation)
      const checkRes = await api.get('/payments', { params: { student: id, limit: 1 } });
      const payments = checkRes.data?.data || [];
      
      if (payments.length > 0) {
        // Student has payments - check if any have transactions (receipts)
        const hasReceipts = payments.some(p => p.transactions && p.transactions.length > 0);
        
        if (hasReceipts) {
          // 🔒 BLOCKED: Student has payment receipts
          const receiptCount = payments.reduce((sum, p) => sum + (p.transactions?.length || 0), 0);
          return Swal.fire({
            icon: 'error',
            title: 'Cannot Delete Student',
            html: `<b>${name}</b> has <b>${receiptCount} payment receipt(s)</b>.<br/><br/>` +
                  `Receipts are permanent financial records and cannot be deleted.<br/><br/>` +
                  `<span style="color:#64748b;font-size:0.9rem">💡 Tip: You can deactivate the student instead to hide them from active lists while preserving all financial records.</span>`,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'OK, Got it'
          });
        } else {
          // Has pending payments but no receipts - show warning
          await Swal.fire({
            icon: 'info',
            title: 'Student Has Pending Fees',
            html: `<b>${name}</b> has <b>${payments.length} pending fee record(s)</b> (no payments made yet).<br/><br/>` +
                  `These pending fees will also be deleted.`,
            confirmButtonColor: '#3498db',
            confirmButtonText: 'OK'
          });
        }
      }
      
      // ✅ STEP 2: Now show delete confirmation
      const result = await Swal.fire({
        title: 'Delete Student?',
        html: `This will permanently delete <b>${name}</b>.<br/><br/>` +
              `<span style="color:#dc2626;font-weight:600">⚠️ This action cannot be undone!</span>`,
        icon: 'warning', 
        showCancelButton: true,
        confirmButtonColor: '#ef4444', 
        cancelButtonColor: '#94a3b8',
        confirmButtonText: 'Yes, Delete Permanently',
        cancelButtonText: 'Cancel'
      });
      
      if (!result.isConfirmed) return;
      
      // ✅ STEP 3: Proceed with deletion
      await api.delete(`/students/${id}`);
      setStudents(prev => prev.filter(st => st._id !== id));
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Student record removed successfully.',
        confirmButtonColor: '#10b981',
        timer: 2000
      });
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to delete student.';
      const hint = err.response?.data?.hint || '';
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        html: `${errorMsg}<br/><br/>${hint ? `<span style="color:#64748b;font-size:0.9rem">${hint}</span>` : ''}`,
        confirmButtonColor: '#ef4444'
      });
    }
  };

  return (
    <div className="view-student-data-container">

      <div className="view-student-data-header">
        <h2 className="view-student-data-title">Student Records</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {!viewOnly && (
            <button 
              className="view-student-data-export-btn" 
              onClick={() => navigate(`${basePath}/students/add`)}
              style={{ background: '#10b981', borderColor: '#10b981' }}
              title="Add New Student"
            >
              <FaUserPlus /> Add Student
            </button>
          )}
          <button 
            className="view-student-data-export-btn" 
            onClick={handleExport}
            disabled={filteredStudents.length === 0}
            title="Export to Excel"
          >
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      <div className="view-student-data-filters">
        {isAdmin && (
          <div className="view-student-data-filter-group">
            <label>College</label>
            <select value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)}>
              <option value="">-- Select College --</option>
              {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        )}
        <div className="view-student-data-filter-group">
          <label>Session</label>
          <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)} disabled={!selectedCollege}>
            <option value="">All Sessions</option>
            {sessions.map(s => <option key={s._id} value={s._id}>{s.name}{s.isActive ? ' ✓' : ''}</option>)}
          </select>
        </div>
        <div className="view-student-data-filter-group">
          <label>Class</label>
          <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} disabled={!selectedCollege}>
            <option value="">All Classes</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name}{c.medium ? ` (${c.medium})` : ''}</option>)}
          </select>
        </div>
        <div className="view-student-data-filter-group view-student-data-search-group">
          <label>Search</label>
          <div className="view-student-data-search-wrap">
            <FaSearch className="view-student-data-search-icon" />
            <input 
              type="text" 
              placeholder="Search by name or Admission No..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
            />
          </div>
        </div>
      </div>

      {selectedCollege && !loading && (
        <div className="view-student-data-count">Total: <b>{filteredStudents.length}</b> student{filteredStudents.length !== 1 ? 's' : ''} found</div>
      )}

      <div className="view-student-data-table-wrapper">
        <table className="view-student-data-table">
          <thead>
            <tr>
              <th className="view-student-data-col-sno">S.No</th>
              <th className="view-student-data-col-photo">Photo</th>
              <th className="view-student-data-col-name view-student-data-align-left">Name</th>
              <th className="view-student-data-col-admno">Admission No</th>
              <th className="view-student-data-col-class">Class</th>
              <th className="view-student-data-col-session">Session</th>
              <th className="view-student-data-col-year">Current Year</th>
              <th className="view-student-data-col-status">Status</th>
              <th className="view-student-data-col-doc">Edu. Doc</th>
              <th className="view-student-data-col-action">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colSpanFull} className="view-student-data-empty">Loading...</td></tr>
            ) : !selectedCollege ? (
              <tr><td colSpan={colSpanFull} className="view-student-data-empty">Please select a college.</td></tr>
            ) : filteredStudents.length === 0 ? (
              <tr><td colSpan={colSpanFull} className="view-student-data-empty">No students found.</td></tr>
            ) : filteredStudents.map((s, i) => {
              const isExpanded = expandedRow === s._id;
              const photoUrl = s.photo ? `${API_BASE}/${s.photo}` : null;

              return (
                <React.Fragment key={s._id}>
                  <tr className="view-student-data-row">

                    <td className="view-student-data-col-sno">{i + 1}</td>

                    {/* Photo Column - Restored perfectly to 125x125 */}
                    <td className="view-student-data-col-photo">
                      <div className="view-student-data-photo-wrap">
                        {photoUrl ? (
                          <img src={photoUrl} alt={s.name} className="view-student-data-photo"
                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                        ) : null}
                        <div className="view-student-data-photo-fallback" style={{ display: photoUrl ? 'none' : 'flex' }}>
                          <FaUserGraduate />
                        </div>
                      </div>
                    </td>

                    {/* Name Column */}
                    <td className="view-student-data-col-name view-student-data-align-left">
                      <div className="view-student-data-name-cell">
                        <span className="view-student-data-name">{s.name}</span>
                        <button className="view-student-data-expand-btn" onClick={() => setExpandedRow(isExpanded ? null : s._id)}>
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>

                    <td className="view-student-data-col-admno"><span className="view-student-data-admno">{s.admissionNo || '—'}</span></td>
                    <td className="view-student-data-col-class"><span className="view-student-data-badge-class">{s.class?.name || s.className || '—'}</span></td>
                    <td className="view-student-data-col-session"><span className="view-student-data-badge-session">{s.session?.name || '—'}</span></td>
                    <td className="view-student-data-col-year"><span className="view-student-data-badge-year">{getStudentYear(s)}</span></td>

                    {/* Status Column */}
                    <td className="view-student-data-col-status">
                      {viewOnly ? (
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: s.isActive ? '#27ae60' : '#e74c3c' }}>
                          {s.isActive ? 'Active' : 'Inactive'}
                        </span>
                      ) : (
                        <>
                          <label className="view-student-data-switch" title={s.isActive ? 'Click to disable' : 'Click to enable'}>
                            <input type="checkbox" checked={!!s.isActive} onChange={() => handleToggleStatus(s)} />
                            <span className="view-student-data-slider" />
                          </label>
                          <div style={{ fontSize: '0.7rem', marginTop: '4px', color: s.isActive ? '#27ae60' : '#e74c3c', fontWeight: '600' }}>
                            {s.isActive ? 'Active' : 'Inactive'}
                          </div>
                        </>
                      )}
                    </td>

                    {/* Education Document Column */}
                    <td className="view-student-data-col-doc">
                      {s.educationDocument ? (
                        <button 
                          className="view-student-data-btn view-student-data-btn-pdf" 
                          title="View Education Document"
                          onClick={() => window.open(`${API_BASE}/${s.educationDocument}`, '_blank')}
                        >
                          <FaFilePdf />
                        </button>
                      ) : (
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>—</span>
                      )}
                    </td>

                    {/* Actions Column */}
                    <td className="view-student-data-col-action">
                      <div className="view-student-data-actions">
                        <button className="view-student-data-btn view" title="View Profile" onClick={() => navigate(`${basePath}/students/data/${s._id}`)}>
                          <FaEye />
                        </button>
                        {!viewOnly && (
                          <>
                            <button className="view-student-data-btn edit" title="Edit Student" onClick={() => navigate(`${basePath}/students/view/${s._id}`)}>
                              <FaEdit />
                            </button>
                            <button className="view-student-data-btn delete" title="Delete" onClick={() => handleDelete(s._id, s.name)}>
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </td>

                  </tr>

                  {/* Expanded Row for Mobile Views */}
                  {isExpanded && (
                    <tr className="view-student-data-expanded-row">
                      <td colSpan={colSpanFull}>
                        <div className="view-student-data-expanded-content">
                          
                          <div className="view-student-data-exp-item view-student-data-exp-photo">
                            <span className="view-student-data-exp-label">Photo</span>
                            <div className="view-student-data-photo-wrap">
                              {photoUrl ? (
                                <img src={photoUrl} alt={s.name} className="view-student-data-photo"
                                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                              ) : null}
                              <div className="view-student-data-photo-fallback" style={{ display: photoUrl ? 'none' : 'flex' }}>
                                <FaUserGraduate />
                              </div>
                            </div>
                          </div>

                          <div className="view-student-data-exp-item view-student-data-exp-admno">
                            <span className="view-student-data-exp-label">Admission No</span>
                            <span className="view-student-data-exp-val view-student-data-admno">{s.admissionNo || '—'}</span>
                          </div>

                          <div className="view-student-data-exp-item view-student-data-exp-class">
                            <span className="view-student-data-exp-label">Class</span>
                            <span className="view-student-data-exp-val"><span className="view-student-data-badge-class">{s.class?.name || s.className || '—'}</span></span>
                          </div>

                          <div className="view-student-data-exp-item view-student-data-exp-session">
                            <span className="view-student-data-exp-label">Session</span>
                            <span className="view-student-data-exp-val"><span className="view-student-data-badge-session">{s.session?.name || '—'}</span></span>
                          </div>

                          <div className="view-student-data-exp-item view-student-data-exp-year">
                            <span className="view-student-data-exp-label">Current Year</span>
                            <span className="view-student-data-exp-val">{getStudentYear(s)}</span>
                          </div>

                          <div className="view-student-data-exp-item view-student-data-exp-status" style={{ alignItems: 'center' }}>
                            <span className="view-student-data-exp-label">Status</span>
                            <label className="view-student-data-switch">
                              <input type="checkbox" checked={!!s.isActive} onChange={() => handleToggleStatus(s)} />
                              <span className="view-student-data-slider" />
                            </label>
                            <div style={{ fontSize: '0.75rem', marginTop: '4px', color: s.isActive ? '#27ae60' : '#e74c3c', fontWeight: '600' }}>
                              {s.isActive ? 'Active' : 'Inactive'}
                            </div>
                          </div>

                          <div className="view-student-data-exp-item view-student-data-exp-doc">
                            <span className="view-student-data-exp-label">Edu. Document</span>
                            {s.educationDocument ? (
                              <button 
                                className="view-student-data-btn view-student-data-btn-pdf" 
                                title="View Education Document"
                                onClick={() => window.open(`${API_BASE}/${s.educationDocument}`, '_blank')}
                                style={{ width: 'auto', padding: '8px 16px', fontSize: '0.85rem', gap: '6px' }}
                              >
                                <FaFilePdf /> View PDF
                              </button>
                            ) : (
                              <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Not Uploaded</span>
                            )}
                          </div>

                          <div className="view-student-data-exp-item view-student-data-exp-action">
                            <span className="view-student-data-exp-label">Actions</span>
                            <div className="view-student-data-actions" style={{ justifyContent: 'center' }}>
                              <button className="view-student-data-btn view" title="View Profile" onClick={() => navigate(`${basePath}/students/data/${s._id}`)}>
                                <FaEye />
                              </button>
                              {!viewOnly && (
                                <>
                                  <button className="view-student-data-btn edit" title="Edit Student" onClick={() => navigate(`${basePath}/students/view/${s._id}`)}>
                                    <FaEdit />
                                  </button>
                                  <button className="view-student-data-btn delete" title="Delete" onClick={() => handleDelete(s._id, s.name)}>
                                    <FaTrash />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default ViewStudent;